import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface PaymentRow {
  id: string;
  status: string;
  amount: number;
  gateway: string | null;
  payment_method: string | null;
  gateway_transaction_id: string | null;
  gateway_response: string | null;
  paid_at: string | null;
  created_at: string;
  billing_date: string | null;
  attempts: number | null;
  plans?: { name: string } | null;
}

type FilterStatus = 'all' | 'approved' | 'pending' | 'failed' | 'refunded';

const STATUS_META: Record<string, { label: string; icon: string; textColor: string; bgColor: string; borderColor: string; dotColor: string }> = {
  approved: {
    label: 'Aprovado', icon: 'ri-checkbox-circle-fill',
    textColor: 'text-emerald-700', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200', dotColor: 'bg-emerald-400',
  },
  pending: {
    label: 'Em análise', icon: 'ri-time-fill',
    textColor: 'text-yellow-700', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', dotColor: 'bg-yellow-400',
  },
  failed: {
    label: 'Recusado', icon: 'ri-close-circle-fill',
    textColor: 'text-red-700', bgColor: 'bg-red-50', borderColor: 'border-red-200', dotColor: 'bg-red-400',
  },
  refunded: {
    label: 'Estornado', icon: 'ri-refund-2-line',
    textColor: 'text-gray-600', bgColor: 'bg-gray-100', borderColor: 'border-gray-200', dotColor: 'bg-gray-400',
  },
};

const getStatusMeta = (s: string) => STATUS_META[s] ?? {
  label: s, icon: 'ri-information-line',
  textColor: 'text-gray-600', bgColor: 'bg-gray-100', borderColor: 'border-gray-200', dotColor: 'bg-gray-300',
};

const fmtCurrency = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTime = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  } as Intl.DateTimeFormatOptions);
};

const parseFailureReason = (gwResponse: string | null): string | null => {
  if (!gwResponse) return null;
  try {
    const gw = JSON.parse(gwResponse);
    return gw?.failure_reason ?? gw?.status_detail ?? gw?.description ?? null;
  } catch {
    return null;
  }
};

const METHOD_LABELS: Record<string, string> = {
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  pix: 'PIX',
  boleto: 'Boleto',
  account_money: 'Saldo',
};

export const ClientPaymentsTab = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from('payments')
      .select('id, status, amount, gateway, payment_method, gateway_transaction_id, gateway_response, paid_at, created_at, billing_date, attempts, plans(name)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);
    setPayments((data as PaymentRow[]) || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const filtered = filter === 'all' ? payments : payments.filter(p => p.status === filter);

  const stats = {
    approved: payments.filter(p => p.status === 'approved').length,
    pending: payments.filter(p => p.status === 'pending').length,
    failed: payments.filter(p => p.status === 'failed').length,
    totalPaid: payments.filter(p => p.status === 'approved').reduce((a, p) => a + Number(p.amount), 0),
  };

  const filterTabs: { id: FilterStatus; label: string; count: number }[] = [
    { id: 'all', label: 'Todos', count: payments.length },
    { id: 'approved', label: 'Aprovados', count: stats.approved },
    { id: 'pending', label: 'Em análise', count: stats.pending },
    { id: 'failed', label: 'Recusados', count: stats.failed },
  ];

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-emerald-600 text-sm" />
            </div>
            <span className="text-xs text-gray-500 font-500">Total pago</span>
          </div>
          <p className="text-lg font-700 text-gray-900">{fmtCurrency(stats.totalPaid)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{stats.approved} pagamentos aprovados</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-yellow-50 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-yellow-600 text-sm" />
            </div>
            <span className="text-xs text-gray-500 font-500">Em análise</span>
          </div>
          <p className="text-lg font-700 text-gray-900">{stats.pending}</p>
          <p className="text-xs text-gray-400 mt-0.5">aguardando confirmação</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
              <i className="ri-close-circle-line text-red-500 text-sm" />
            </div>
            <span className="text-xs text-gray-500 font-500">Recusados</span>
          </div>
          <p className="text-lg font-700 text-gray-900">{stats.failed}</p>
          <p className="text-xs text-gray-400 mt-0.5">tentativas sem sucesso</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 px-2 pt-2">
          {filterTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-500 rounded-t-lg transition-all cursor-pointer whitespace-nowrap ${filter === tab.id ? 'text-emerald-700 border-b-2 border-emerald-500 font-700 -mb-px bg-emerald-50/50' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-[11px] font-700 px-1.5 py-0.5 rounded-full ${filter === tab.id ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-14 text-center">
            <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <i className="ri-receipt-line text-gray-300 text-2xl" />
            </div>
            <p className="text-sm font-600 text-gray-400">
              {filter === 'all' ? 'Nenhum pagamento encontrado' : `Nenhum pagamento ${getStatusMeta(filter).label.toLowerCase()} encontrado`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map(payment => {
              const meta = getStatusMeta(payment.status);
              const failureReason = parseFailureReason(payment.gateway_response);
              const isExpanded = expandedId === payment.id;
              const methodLabel = payment.payment_method ? (METHOD_LABELS[payment.payment_method] ?? payment.payment_method) : null;

              return (
                <div key={payment.id} className="transition-colors hover:bg-gray-50/60">
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : payment.id)}
                    className="w-full flex items-center gap-4 px-5 py-4 text-left cursor-pointer"
                  >
                    {/* Status icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.bgColor}`}>
                      <i className={`${meta.icon} text-base ${meta.textColor}`} />
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-600 text-gray-800">{payment.plans?.name ?? 'Assinatura'}</p>
                        <span className={`text-[11px] font-700 px-1.5 py-0.5 rounded-full border ${meta.bgColor} ${meta.textColor} ${meta.borderColor}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p className="text-xs text-gray-400">
                          {fmtDate(payment.paid_at ?? payment.created_at)}
                        </p>
                        {methodLabel && (
                          <>
                            <span className="text-gray-200 text-xs">·</span>
                            <p className="text-xs text-gray-400">{methodLabel}</p>
                          </>
                        )}
                        {payment.gateway && (
                          <>
                            <span className="text-gray-200 text-xs">·</span>
                            <p className="text-xs text-gray-400 capitalize">{payment.gateway}</p>
                          </>
                        )}
                      </div>
                      {/* Failure reason — visible inline for failed payments */}
                      {payment.status === 'failed' && failureReason && (
                        <p className="text-xs text-red-500 mt-0.5 font-500">{failureReason}</p>
                      )}
                    </div>

                    {/* Amount + expand */}
                    <div className="text-right shrink-0 flex items-center gap-3">
                      <div>
                        <p className={`text-sm font-700 ${payment.status === 'failed' ? 'text-red-500 line-through opacity-60' : 'text-gray-900'}`}>
                          {fmtCurrency(Number(payment.amount))}
                        </p>
                      </div>
                      <i className={`text-gray-300 text-sm transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className={`mx-5 mb-4 p-4 rounded-xl border ${meta.borderColor} ${meta.bgColor}`}>
                      <div className="grid grid-cols-2 gap-3">
                        {payment.gateway_transaction_id && (
                          <div>
                            <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wide mb-0.5">ID da transação</p>
                            <p className="text-xs font-600 text-gray-700 font-mono break-all">{payment.gateway_transaction_id}</p>
                          </div>
                        )}
                        {payment.gateway && (
                          <div>
                            <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wide mb-0.5">Gateway</p>
                            <p className="text-xs font-600 text-gray-700 capitalize">{payment.gateway}</p>
                          </div>
                        )}
                        {methodLabel && (
                          <div>
                            <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wide mb-0.5">Forma de pagamento</p>
                            <p className="text-xs font-600 text-gray-700">{methodLabel}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wide mb-0.5">Data/hora</p>
                          <p className="text-xs font-600 text-gray-700">{fmtDateTime(payment.paid_at ?? payment.created_at)}</p>
                        </div>
                        {payment.billing_date && (
                          <div>
                            <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wide mb-0.5">Data de cobrança</p>
                            <p className="text-xs font-600 text-gray-700">{fmtDate(payment.billing_date)}</p>
                          </div>
                        )}
                        {payment.attempts !== null && payment.attempts > 0 && (
                          <div>
                            <p className="text-[11px] font-600 text-gray-400 uppercase tracking-wide mb-0.5">Tentativas</p>
                            <p className="text-xs font-600 text-gray-700">{payment.attempts}</p>
                          </div>
                        )}
                        {payment.status === 'failed' && failureReason && (
                          <div className="col-span-2">
                            <p className="text-[11px] font-600 text-red-500 uppercase tracking-wide mb-0.5">Motivo da recusa</p>
                            <p className="text-xs font-600 text-red-600">{failureReason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
